import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'
import { useAdmin } from '../context/AdminContext'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'
const PURPLE = '#7C3AED'

const MONTHS_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December']
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function getStatus(actual, goal) {
  if (!goal || goal === 0) return null
  const pct = (actual / goal) * 100
  if (pct >= 100) return { pct, color: GREEN,  label: 'Goal met!',      emoji: '✅' }
  if (pct >= 80)  return { pct, color: '#EAB308', label: 'Almost there', emoji: '🟡' }
  if (pct >= 50)  return { pct, color: AMBER,  label: 'Needs effort',    emoji: '🟠' }
  return             { pct, color: RED,    label: 'Behind target',    emoji: '🔴' }
}

function CircleProgress({ pct, size = 80, stroke = 7, color = BLUE }) {
  const r      = (size - stroke) / 2
  const circ   = 2 * Math.PI * r
  const offset = circ - (Math.min(100, pct || 0) / 100) * circ
  return (
    <svg width={size} height={size} viewBox={'0 0 ' + size + ' ' + size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={BORDER} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform={'rotate(-90 ' + size/2 + ' ' + size/2 + ')'} />
    </svg>
  )
}

function inp(focused) {
  return { width: '100%', border: '1px solid ' + (focused ? BLUE : BORDER), borderRadius: '9px', padding: '8px 12px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', boxSizing: 'border-box' }
}

function NumField({ label, value, onChange, placeholder }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '4px' }}>{label}</label>
      <input type="number" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={inp(focused)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
    </div>
  )
}

function AdminGoals() {
  const { admin }      = useAdmin()
  const currentYear    = new Date().getFullYear()
  const currentMonth   = new Date().getMonth() + 1
  const canEdit        = admin?.role === 'super_admin' || admin?.role === 'admin' || admin?.role === 'finance'

  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [goals, setGoals]               = useState({})
  const [actuals, setActuals]           = useState({})
  const [lastYearActuals, setLastYearActuals] = useState({})
  const [notes, setNotes]               = useState({})
  const [loading, setLoading]           = useState(true)
  const [saving, setSaving]             = useState(false)
  const [saved, setSaved]               = useState(false)
  const [expandedMonth, setExpandedMonth] = useState(currentMonth)

  useEffect(() => { fetchAll() }, [selectedYear]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActualsForYear = async (year) => {
    const start = new Date(year, 0, 1).toISOString()
    const end   = new Date(year, 11, 31, 23, 59, 59).toISOString()
    const [{ data: incomeData }, { data: sellersData }] = await Promise.all([
      supabase.from('admin_income').select('amount,received_at,stream').gte('received_at', start).lte('received_at', end),
      supabase.from('sellers').select('created_at,subscription_plan,plan').gte('created_at', start).lte('created_at', end),
    ])
    const map = {}
    incomeData?.forEach(i => {
      const m = new Date(i.received_at).getMonth() + 1
      if (!map[m]) map[m] = { revenue: 0, subscribers: 0, trials: 0 }
      if (i.stream === 'subscription') map[m].revenue += Number(i.amount || 0)
    })
    sellersData?.forEach(s => {
      const m = new Date(s.created_at).getMonth() + 1
      if (!map[m]) map[m] = { revenue: 0, subscribers: 0, trials: 0 }
      map[m].trials += 1
      if (['starter','growth','pro','business'].includes(s.subscription_plan || s.plan)) map[m].subscribers += 1
    })
    return map
  }

  const fetchAll = async () => {
    setLoading(true)
    const { data: goalsData } = await supabase.from('admin_goals').select('*').eq('year', selectedYear)
    const goalsMap = {}
    const notesMap = {}
    goalsData?.forEach(g => {
      if (g.month !== null) {
        goalsMap[g.month] = { revenue_goal: g.revenue_goal || '', subscriber_goal: g.subscriber_goal || '', trial_goal: g.trial_goal || '', conversion_goal: g.conversion_goal || '' }
        if (g.notes) notesMap[g.month] = g.notes
      }
    })
    setGoals(goalsMap)
    setNotes(notesMap)
    const [curr, last] = await Promise.all([fetchActualsForYear(selectedYear), fetchActualsForYear(selectedYear - 1)])
    setActuals(curr)
    setLastYearActuals(last)
    setLoading(false)
  }

  const handleSave = async () => {
    if (!canEdit) return
    setSaving(true)
    for (let m = 1; m <= 12; m++) {
      const g = goals[m] || {}
      await supabase.from('admin_goals').upsert({
        year: selectedYear, month: m,
        revenue_goal: Number(g.revenue_goal) || 0, subscriber_goal: Number(g.subscriber_goal) || 0,
        trial_goal: Number(g.trial_goal) || 0, conversion_goal: Number(g.conversion_goal) || 0,
        notes: notes[m] || null,
      }, { onConflict: 'year,month' })
    }
    await fetchAll()
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const fmt = (n) => {
    const v = Math.abs(Math.round(n || 0))
    if (v >= 1000000) return '৳' + (v / 1000000).toFixed(1) + 'M'
    if (v >= 100000)  return '৳' + Math.round(v / 1000) + 'k'
    return '৳' + v.toLocaleString()
  }

  const yearlyGoalRev  = Object.values(goals).reduce((s, g) => s + (Number(g.revenue_goal) || 0), 0)
  const yearlyGoalSub  = Object.values(goals).reduce((s, g) => s + (Number(g.subscriber_goal) || 0), 0)
  const yearlyActRev   = Object.values(actuals).reduce((s, m) => s + (m.revenue || 0), 0)
  const yearlyActSub   = Object.values(actuals).reduce((s, m) => s + (m.subscribers || 0), 0)
  const lastYearRev    = Object.values(lastYearActuals).reduce((s, m) => s + (m.revenue || 0), 0)
  const yearlyRevPct   = yearlyGoalRev > 0 ? (yearlyActRev / yearlyGoalRev) * 100 : 0
  const yearlySubPct   = yearlyGoalSub > 0 ? (yearlyActSub / yearlyGoalSub) * 100 : 0
  const yoyGrowth      = lastYearRev > 0 ? ((yearlyActRev - lastYearRev) / lastYearRev) * 100 : null

  const currActual = actuals[currentMonth] || { revenue: 0, subscribers: 0, trials: 0 }
  const currGoal   = goals[currentMonth]   || {}
  const currRevSt  = getStatus(currActual.revenue, Number(currGoal.revenue_goal))
  const currSubSt  = getStatus(currActual.subscribers, Number(currGoal.subscriber_goal))

  const streak = (() => {
    let count = 0
    for (let m = currentMonth; m >= 1; m--) {
      const g = Number(goals[m]?.revenue_goal) || 0
      const a = actuals[m]?.revenue || 0
      if (g > 0 && a >= g) count++
      else break
    }
    return count
  })()

  // Chart data — 12 months
  const chartData = MONTHS_SHORT.map((label, i) => {
    const m = i + 1
    return {
      month: label,
      revenue: Math.round(actuals[m]?.revenue || 0),
      goal:    Number(goals[m]?.revenue_goal) || 0,
    }
  })

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>
          <style>{`
            @media(max-width:960px){ .goals-grid{grid-template-columns:1fr!important} }
            @media(max-width:600px){ .goals-kpi{grid-template-columns:1fr!important} }
          `}</style>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Goals</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>Monthly and yearly revenue targets</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {streak > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '20px', padding: '6px 12px' }}>
                  <span>🔥</span>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#C2410C' }}>{streak} month streak</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: '4px' }}>
                {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                  <button key={y} onClick={() => setSelectedYear(y)}
                    style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid ' + (selectedYear === y ? BLUE : BORDER), background: selectedYear === y ? BLUE : WHITE, color: selectedYear === y ? WHITE : MUTED, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                    {y}
                  </button>
                ))}
              </div>
              {canEdit && (
                <button onClick={handleSave} disabled={saving}
                  style={{ padding: '8px 18px', borderRadius: '9px', background: saving ? BORDER : BLUE, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                  {saving ? 'Saving...' : 'Save goals'}
                </button>
              )}
            </div>
          </div>

          {saved && (
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '12.5px', color: GREEN, fontWeight: 600 }}>
              Goals saved successfully!
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: MUTED, fontSize: '13px' }}>Loading goals...</div>
          )}

          {!loading && selectedYear === currentYear && (
            <div style={{ background: 'linear-gradient(135deg, ' + BLUE + ' 0%, ' + PURPLE + ' 100%)', borderRadius: '14px', padding: '24px', color: WHITE, marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>This month — {MONTHS_FULL[currentMonth - 1]}</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.5px' }}>{fmt(currActual.revenue)}</div>
                  {currRevSt && <div style={{ fontSize: '12px', marginTop: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', padding: '3px 10px', display: 'inline-block' }}>{currRevSt.emoji} {currRevSt.label}</div>}
                </div>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <CircleProgress pct={currRevSt?.pct || 0} size={80} stroke={7} color={currRevSt?.color || 'rgba(255,255,255,0.3)'} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800 }}>
                    {currRevSt ? Math.round(currRevSt.pct) + '%' : '—'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginBottom: '14px' }}>
                {[
                  { label: 'Revenue', val: fmt(currActual.revenue), sub: Number(currGoal.revenue_goal) > 0 ? 'of ' + fmt(Number(currGoal.revenue_goal)) : 'no goal set' },
                  { label: 'New paid', val: currActual.subscribers, sub: Number(currGoal.subscriber_goal) > 0 ? 'of ' + currGoal.subscriber_goal + ' goal' : 'no goal' },
                  { label: 'Trials', val: currActual.trials, sub: Number(currGoal.trial_goal) > 0 ? 'of ' + currGoal.trial_goal + ' goal' : 'no goal' },
                ].map(s => (
                  <div key={s.label} style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '10px', padding: '12px' }}>
                    <div style={{ fontSize: '10px', opacity: 0.7, marginBottom: '4px' }}>{s.label}</div>
                    <div style={{ fontSize: '17px', fontWeight: 800 }}>{s.val}</div>
                    <div style={{ fontSize: '10px', opacity: 0.6, marginTop: '2px' }}>{s.sub}</div>
                  </div>
                ))}
              </div>
              {Number(currGoal.revenue_goal) > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[
                    { label: 'Revenue', st: currRevSt, actual: currActual.revenue, goal: Number(currGoal.revenue_goal) },
                    { label: 'Subscribers', st: currSubSt, actual: currActual.subscribers, goal: Number(currGoal.subscriber_goal) },
                  ].map(bar => bar.goal > 0 && (
                    <div key={bar.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>
                        <span>{bar.label}</span>
                        <span>{Math.round(Math.min(100, (bar.actual / bar.goal) * 100))}%</span>
                      </div>
                      <div style={{ height: '6px', background: 'rgba(255,255,255,0.2)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: Math.min(100, (bar.actual / bar.goal) * 100) + '%', background: WHITE, borderRadius: '3px', transition: 'width 0.4s' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Year overview */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '18px 20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '16px' }}>{selectedYear} Year Overview</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }} className="goals-kpi">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '8px' }}>
                      <CircleProgress pct={yearlyRevPct} size={90} stroke={8} color={BLUE} />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, color: TEXT }}>
                        {Math.round(yearlyRevPct)}%
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: MUTED, marginBottom: '2px' }}>Revenue</div>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: TEXT }}>{fmt(yearlyActRev)}</div>
                    {yearlyGoalRev > 0 && <div style={{ fontSize: '11px', color: MUTED }}>of {fmt(yearlyGoalRev)}</div>}
                    {yoyGrowth !== null && (
                      <div style={{ fontSize: '11.5px', fontWeight: 700, color: yoyGrowth >= 0 ? GREEN : RED, marginTop: '4px' }}>
                        {yoyGrowth >= 0 ? '+' : ''}{Math.round(yoyGrowth)}% vs {selectedYear - 1}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '8px' }}>
                      <CircleProgress pct={yearlySubPct} size={90} stroke={8} color="#EC4899" />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, color: TEXT }}>
                        {Math.round(yearlySubPct)}%
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: MUTED, marginBottom: '2px' }}>Subscribers</div>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: TEXT }}>{yearlyActSub}</div>
                    {yearlyGoalSub > 0 && <div style={{ fontSize: '11px', color: MUTED }}>of {yearlyGoalSub} goal</div>}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ width: '90px', height: '90px', borderRadius: '50%', background: streak > 0 ? '#FFFBEB' : BG, border: '2px solid ' + (streak > 0 ? AMBER : BORDER), display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', flexDirection: 'column' }}>
                      <div style={{ fontSize: '24px' }}>{streak > 0 ? '🔥' : '—'}</div>
                      {streak > 0 && <div style={{ fontSize: '11px', fontWeight: 800, color: AMBER }}>{streak}mo</div>}
                    </div>
                    <div style={{ fontSize: '11px', color: MUTED, marginBottom: '2px' }}>Win streak</div>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: TEXT }}>{streak > 0 ? streak + ' months' : 'No streak'}</div>
                  </div>
                </div>

                {/* Revenue vs Goal bar chart */}
                <div style={{ marginBottom: '4px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: TEXT, marginBottom: '10px' }}>Revenue vs goal — monthly</div>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: MUTED }} width={44} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? '৳' + Math.round(v/1000) + 'k' : '৳' + v} />
                      <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '10px', fontSize: '11px', fontFamily: 'Space Grotesk' }} formatter={(v, name) => [fmt(v), name === 'revenue' ? 'Actual' : 'Goal']} />
                      <Bar dataKey="goal"    fill={BORDER}  radius={[4, 4, 0, 0]} />
                      <Bar dataKey="revenue" fill={BLUE}    radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '6px' }}>
                    {[{ color: BLUE, label: 'Actual' }, { color: BORDER, label: 'Goal' }].map(l => (
                      <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '14px', height: '3px', background: l.color, borderRadius: '2px' }} />
                        <span style={{ fontSize: '11px', color: MUTED }}>{l.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Monthly breakdown */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Monthly goals</div>
                  <div style={{ fontSize: '11.5px', color: MUTED }}>Click a month to expand and edit</div>
                </div>
                <div>
                  {MONTHS_FULL.map((month, idx) => {
                    const m         = idx + 1
                    const goal      = goals[m]   || {}
                    const actual    = actuals[m]  || { revenue: 0, subscribers: 0, trials: 0 }
                    const lastAct   = lastYearActuals[m] || { revenue: 0, subscribers: 0, trials: 0 }
                    const revGoal   = Number(goal.revenue_goal) || 0
                    const revSt     = getStatus(actual.revenue, revGoal)
                    const isFuture  = selectedYear > currentYear || (selectedYear === currentYear && m > currentMonth)
                    const isCurrent = selectedYear === currentYear && m === currentMonth
                    const isOpen    = expandedMonth === m

                    return (
                      <div key={m} style={{ borderBottom: idx < 11 ? '1px solid #F5F5F5' : 'none' }}>
                        {/* Row header */}
                        <div
                          onClick={() => setExpandedMonth(isOpen ? null : m)}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', cursor: 'pointer', background: isCurrent ? '#EFF6FF' : WHITE, borderLeft: isCurrent ? '3px solid ' + BLUE : '3px solid transparent', transition: 'background 0.1s' }}
                          onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = BG }}
                          onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = WHITE }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isFuture ? BORDER : (revSt?.color || BORDER), flexShrink: 0 }} />
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: isCurrent ? BLUE : TEXT }}>{MONTHS_SHORT[idx]}</span>
                                {isCurrent && <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', background: BLUE, color: WHITE }}>Now</span>}
                                {!isFuture && revSt && revGoal > 0 && <span style={{ fontSize: '11px', color: revSt.color, fontWeight: 600 }}>{revSt.emoji} {revSt.label}</span>}
                                {isFuture && <span style={{ fontSize: '11px', color: MUTED }}>Upcoming</span>}
                              </div>
                              {revGoal > 0 && !isFuture && (
                                <div style={{ marginTop: '3px', width: '100px', height: '4px', background: BG, borderRadius: '2px', overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: Math.min(100, (actual.revenue / revGoal) * 100) + '%', background: revSt?.color || MUTED, borderRadius: '2px' }} />
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT }}>{fmt(actual.revenue)}</div>
                              <div style={{ fontSize: '11px', color: MUTED }}>{actual.subscribers} paid · {actual.trials} trials</div>
                            </div>
                            <div style={{ fontSize: '12px', color: MUTED, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</div>
                          </div>
                        </div>

                        {/* Expanded content */}
                        {isOpen && (
                          <div style={{ padding: '16px 18px', background: '#FAFAFA', borderTop: '1px solid ' + BORDER }}>

                            {/* Progress bars */}
                            {revGoal > 0 && !isFuture && (
                              <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {[
                                  { label: 'Revenue', actual: actual.revenue, goal: revGoal, st: revSt },
                                  { label: 'Subscribers', actual: actual.subscribers, goal: Number(goal.subscriber_goal) || 0, st: getStatus(actual.subscribers, Number(goal.subscriber_goal)) },
                                  { label: 'Trials', actual: actual.trials, goal: Number(goal.trial_goal) || 0, st: getStatus(actual.trials, Number(goal.trial_goal)) },
                                ].map(bar => bar.goal > 0 && (
                                  <div key={bar.label}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', marginBottom: '4px' }}>
                                      <span style={{ color: MUTED, fontWeight: 600 }}>{bar.label}</span>
                                      <span style={{ fontWeight: 700, color: bar.st?.color || MUTED }}>
                                        {bar.label === 'Revenue' ? fmt(bar.actual) : bar.actual} / {bar.label === 'Revenue' ? fmt(bar.goal) : bar.goal}
                                        <span style={{ color: MUTED, fontWeight: 400, marginLeft: '6px' }}>({Math.round(Math.min(100, (bar.actual / bar.goal) * 100))}%)</span>
                                      </span>
                                    </div>
                                    <div style={{ height: '7px', background: BORDER, borderRadius: '4px', overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: Math.min(100, (bar.actual / bar.goal) * 100) + '%', background: bar.st?.color || MUTED, borderRadius: '4px', transition: 'width 0.4s' }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* YoY comparison */}
                            {lastAct.revenue > 0 && (
                              <div style={{ background: WHITE, borderRadius: '10px', border: '1px solid ' + BORDER, padding: '12px 14px', marginBottom: '14px' }}>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>vs {selectedYear - 1}</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                  <div>
                                    <div style={{ fontSize: '11px', color: MUTED }}>Last year revenue</div>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{fmt(lastAct.revenue)}</div>
                                    <div style={{ fontSize: '11.5px', fontWeight: 700, color: actual.revenue >= lastAct.revenue ? GREEN : RED }}>
                                      {actual.revenue >= lastAct.revenue ? '+' : ''}{Math.round(((actual.revenue - lastAct.revenue) / lastAct.revenue) * 100)}%
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '11px', color: MUTED }}>Last year subs</div>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{lastAct.subscribers}</div>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '11px', color: MUTED }}>Last year trials</div>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{lastAct.trials}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Goal input fields */}
                            {canEdit && (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                <NumField label="Revenue goal (৳)" value={goals[m]?.revenue_goal || ''} onChange={v => setGoals(g => ({ ...g, [m]: { ...g[m], revenue_goal: v } }))} placeholder="e.g. 50000" />
                                <NumField label="New subscribers goal" value={goals[m]?.subscriber_goal || ''} onChange={v => setGoals(g => ({ ...g, [m]: { ...g[m], subscriber_goal: v } }))} placeholder="e.g. 10" />
                                <NumField label="Trial signups goal" value={goals[m]?.trial_goal || ''} onChange={v => setGoals(g => ({ ...g, [m]: { ...g[m], trial_goal: v } }))} placeholder="e.g. 20" />
                                <NumField label="Conversion goal (%)" value={goals[m]?.conversion_goal || ''} onChange={v => setGoals(g => ({ ...g, [m]: { ...g[m], conversion_goal: v } }))} placeholder="e.g. 40" />
                              </div>
                            )}

                            {/* Quick set from last year */}
                            {lastAct.revenue > 0 && canEdit && (
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '6px' }}>Quick set from last year ({fmt(lastAct.revenue)})</div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  {[0, 20, 50, 100].map(pct => (
                                    <button key={pct}
                                      onClick={() => setGoals(g => ({ ...g, [m]: { ...g[m], revenue_goal: Math.round(lastAct.revenue * (1 + pct / 100)), subscriber_goal: Math.round((lastAct.subscribers || 0) * (1 + pct / 100)) } }))}
                                      style={{ flex: 1, padding: '7px', borderRadius: '8px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '12px', fontWeight: 600, color: BLUE, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                                      {pct === 0 ? 'Same' : '+' + pct + '%'}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Notes */}
                            {canEdit && (
                              <div>
                                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Notes</label>
                                <textarea value={notes[m] || ''} onChange={e => setNotes(n => ({ ...n, [m]: e.target.value }))}
                                  placeholder="e.g. Eid season — expect higher signups..." rows={2}
                                  style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 12px', fontSize: '12px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', resize: 'none', boxSizing: 'border-box' }} />
                              </div>
                            )}

                            {notes[m] && !canEdit && (
                              <div style={{ background: BG, borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: TEXT }}>
                                {notes[m]}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {!canEdit && (
                <div style={{ background: BG, borderRadius: '10px', border: '1px solid ' + BORDER, padding: '12px 16px', textAlign: 'center', fontSize: '12px', color: MUTED }}>
                  View only — contact super admin to update goals
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default AdminGoals